import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "constants/routes";
import { motion } from "framer-motion";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	DotIcon,
	LogoutIcon,
} from "@assets/icons";
import P from "@components/common/P/P";
import { logout } from "services/authService";

// Define the structure for the sidebar items
interface SidebarPlatformItem {
	labelKey: string;
	path?: string; // Optional path for navigation
	children?: SidebarPlatformItem[]; // Optional children for dropdown items
}

// Define the sidebar items in a dictionary array using ROUTES with prefix
const SidebarPlatformItems: SidebarPlatformItem[] = [
	{
		labelKey: "sidebar.platform.companies",
		children: [
			{ labelKey: "sidebar.platform.companiesList", path: ROUTES.COMPANIES },
		],
	},
	{
		labelKey: "sidebar.platform.requests",
		children: [
			{ labelKey: "sidebar.platform.cargoOffers", path: ROUTES.CARGO_OFFERS },
			{
				labelKey: "sidebar.platform.transportOffers",
				path: ROUTES.TRANSPORT_OFFERS,
			},
			{
				labelKey: "sidebar.platform.manageOffers",
				path: ROUTES.MANAGE_OFFERS,
			},
		],
	},
	{
		labelKey: "sidebar.platform.vehicleControl",
		children: [
			{ labelKey: "sidebar.platform.vehicleTypes", path: ROUTES.VEHICLE_TYPES },
			{
				labelKey: "sidebar.platform.vehicleBrands",
				path: ROUTES.VEHICLE_BRANDS,
			},
			{
				labelKey: "sidebar.platform.vehicleModels",
				path: ROUTES.VEHICLE_MODELS,
			},
		],
	},
];

const SidebarPlatform: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);

	// Universal navigation function
	const handleNavigation = (path?: string) => {
		if (path) {
			navigate(path);
		}
	};

	const handleDropdownToggle = (label: string) => {
		setOpenDropdown(openDropdown === label ? null : label);
	};

	// Framer-motion animation variants
	const dropdownVariants = {
		open: {
			opacity: 1,
			height: "auto",
			transition: { duration: 0.4 },
		},
		closed: {
			opacity: 0,
			height: 0,
			transition: { duration: 0.3 },
		},
	};

	const handleLogout = async () => {
		try {
			await logout();
			localStorage.removeItem("access_token");
			localStorage.removeItem("refresh_token");
			localStorage.removeItem("user");
			navigate(ROUTES.LOGIN);
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	return (
		<aside className="flex flex-col justify-between w-64 h-[calc(100vh-70px)] border-r bg-bgColor border-stroke">
			{/* Sidebar Items */}
			<nav className="flex-1 overflow-y-auto">
				<div>
					{SidebarPlatformItems.map((item) => (
						<div key={item.labelKey}>
							{item.children ? (
								<div>
									{/* Dropdown toggle */}
									<div
										className={`grid grid-cols-maxFr gap-2 w-full pr-4 py-2 text-left transition-colors duration-300 rounded-lg cursor-pointer`}
										onClick={() => handleDropdownToggle(item.labelKey)}
									>
										{/* Left Indicator */}
										<div
											className={`w-1 h-11 rounded-tr-md rounded-br-md ${
												openDropdown === item.labelKey
													? "bg-mainColor"
													: "bg-white"
											} transition-colors duration-300`}
										/>

										<div
											className={`grid gap-3 px-3 py-2.5 cursor-pointer rounded-md grid-cols-maxTwo place-content-between place-items-center w-full ${
												openDropdown === item.labelKey ? "bg-mainColor" : ""
											} transition-colors duration-300`}
										>
											<P
												size="t1"
												type="semibold"
												className={
													openDropdown === item.labelKey
														? "text-whiteText"
														: "text-textColor"
												}
											>
												{t(item.labelKey)}
											</P>
											<SvgIcon
												Icon={
													openDropdown === item.labelKey
														? ChevronDownIcon
														: ChevronRightIcon
												}
												width={20}
												height={20}
												className={
													openDropdown === item.labelKey
														? "stroke-white"
														: "stroke-grayThree"
												}
											/>
										</div>
									</div>
									{/* Dropdown Items with animation */}
									<motion.div
										initial={false}
										variants={dropdownVariants}
										animate={openDropdown === item.labelKey ? "open" : "closed"}
										className="ml-2 overflow-hidden"
									>
										<div className="grid grid-flow-row grid-cols-1 gap-2 auto-rows-max">
											{item.children.map((child) => (
												<div
													key={child.labelKey}
													onClick={() => handleNavigation(child.path)}
													className={`py-2 px-4 text-sm rounded-lg transition-colors duration-300 cursor-pointer grid grid-cols-maxTwo gap-4 place-items-center`}
												>
													<SvgIcon
														Icon={DotIcon}
														width={6}
														height={6}
														className={`${
															location.pathname === child.path
																? "fill-mainColor"
																: "fill-grayThree"
														}`}
													/>
													<P
														size="t1"
														type={`${
															location.pathname === child.path
																? "semibold"
																: "regular"
														}`}
														className="cursor-pointer"
													>
														{t(child.labelKey)}
													</P>
												</div>
											))}
										</div>
									</motion.div>
								</div>
							) : (
								// Direct Link for items without children
								<div
									className="grid gap-2 py-2 pr-4 grid-cols-maxFr"
									onClick={() => handleNavigation(item.path)}
								>
									{/* Left Indicator for Non-Dropdown Items */}
									<div
										className={`w-1 h-11 rounded-tr-md rounded-br-md ${
											location.pathname === item.path
												? "bg-mainColor"
												: "bg-white"
										} transition-colors duration-300`}
									/>

									<div
										className={`grid gap-3 px-3 py-2.5 cursor-pointer rounded-md grid-cols-maxTwo place-content-between place-items-center w-full ${
											location.pathname === item.path ? "bg-mainColor" : ""
										} transition-colors duration-300`}
									>
										<P
											size="t1"
											type="semibold"
											className={`${
												location.pathname === item.path
													? "text-white"
													: "text-textColor"
											}`}
										>
											{t(item.labelKey)}
										</P>
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			</nav>

			{/* Logout Button */}
			<div
				className="grid gap-2 px-6 py-4 border-t cursor-pointer border-stroke grid-cols-maxFr"
				onClick={handleLogout}
			>
				<SvgIcon
					Icon={LogoutIcon}
					width={22}
					height={22}
					className="fill-grayThree stroke-grayThree"
				/>
				<P size="p3" type="semibold" className="cursor-pointer text-grayThree">
					{t("sidebar.logout")}
				</P>
			</div>
		</aside>
	);
};

export default React.memo(SidebarPlatform);
